<template>
  <div>
    <div class="row mb-4">
      <div class="col-12">
        <Card cardClasses="border-0 shadow-sm rounded-4">
          <template v-slot:header>
            <div class="d-flex align-items-center justify-content-between p-3">
              <div class="d-flex align-items-center">
                <div class="icon-circle bg-primary text-white me-3">
                  <vue-feather type="credit-card" size="20"></vue-feather>
                </div>
                <div>
                  <h4 class="mb-0 fw-bold text-primary">Billing History</h4>
                  <p class="text-muted mb-0 small">View and manage your subscription payments ({{billings.length}} transactions)</p>
                </div>
              </div>
            </div>
          </template>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-5">
            <Spinner spinner-class="mb-1" />
            <p class="text-muted">Loading billing data...</p>
          </div>

          <!-- Billing Table -->
          <div v-else class="p-3">
            <div class="card border-0 shadow-sm rounded-4">
              <div class="card-header bg-light d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <div class="icon-circle-sm bg-primary text-white me-2">
                    <vue-feather type="list" size="14"></vue-feather>
                  </div>
                  <h5 class="mb-0 fw-bold">Transaction History</h5>
                </div>
                <button class="btn btn-primary btn-sm rounded-pill" @click="openPaymentModal()">
                  <vue-feather type="credit-card" size="14" class="me-1"></vue-feather>
                  Make Payment
                </button>
              </div>
              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table table-hover mb-0">
                    <thead class="table-light">
                      <tr>
                        <th scope="col">Package</th>
                        <th scope="col">Action</th>
                        <th scope="col">Duration</th>
                        <th scope="col">Method</th>
                        <th scope="col">Reference</th>
                        <th scope="col">Amount</th>
                        <th scope="col">Created</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="billing in billings" :key="billing.id">
                        <td>
                          <Badge pill :type="getPackageType(billing.payment.details.package)" class="badge-lg">
                            {{ billing.payment.details.package }}
                          </Badge>
                        </td>
                        <td>{{ billing.payment.details.type }}</td>
                        <td>{{ billing.payment.details.duration }} Months</td>
                        <td>
                          <Badge pill type="info" class="badge-sm">
                            {{ billing.payment.details.method }}
                          </Badge>
                        </td>
                        <td><code>{{ billing.payment.details.reference }}</code></td>
                        <td class="fw-bold">{{ formatNumber(billing.payment.details.amount) }}</td>
                        <td>{{ formatDate(billing.created_at) }}</td>
                        <td>
                          <button ref="btnPrint" class="btn btn-success btn-sm rounded-pill" @click="printReceipt($event, billing)">
                            <vue-feather type="printer" size="14" class="me-1"></vue-feather>
                            Receipt
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Back Button -->
          <div class="d-flex justify-content-center pt-3 pb-4 border-top mx-3">
            <button class="btn btn-outline-dark rounded-pill" @click="$router.go(-1)">
              <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
              Back
            </button>
          </div>
        </Card>
      </div>
    </div>

    <!-- Print Receipt (Hidden) -->
    <div id="printReceipt">
      <div class="row">
        <div class="col-md-12">
          <div class="d-flex align-items-center mb-4">
            <img src="https://resley.tech/images/resley-logo.png" height="75" alt="Resley Tech" class="me-3" />
            <div>
              <h3 class="mb-1">Resley Tech Ltd</h3>
              <p class="mb-0">Oginga Odinga Street, Kisumu</p>
              <p class="mb-0">+254 719 232 782 | info@resley.tech</p>
              <p class="mb-0">https://resley.tech</p>
            </div>
          </div>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-md-12">
          <div class="border-top border-bottom py-3 mb-4">
            <h4 class="text-center mb-0">Payment Receipt</h4>
          </div>

          <div class="table-responsive">
            <table class="table table-bordered">
              <thead class="table-light">
                <tr>
                  <th scope="col">Package</th>
                  <th scope="col">Duration</th>
                  <th scope="col">Method</th>
                  <th scope="col">Reference</th>
                  <th scope="col">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ billing.payment.details.package }}</td>
                  <td>{{ billing.payment.details.duration }} Months</td>
                  <td>{{ billing.payment.details.method }}</td>
                  <td>{{ billing.payment.details.reference }}</td>
                  <td>{{ formatNumber(billing.payment.details.amount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="mt-5 pt-5 border-top">
            <p class="text-center mb-0">Thank you for your business!</p>
          </div>
        </div>
      </div>
    </div>
    <!-- Print Receipt End -->

    <StkModal
      ref="stkModal"
      type="renew"
      :package_id="subscription.package.id"
      :package_name="subscription.package.name"
      :price="subscription.price"
    ></StkModal>
  </div>
</template>


<script>
import subscriptionService from "@/api/subscriptionService";
import formatDate from "../../utils/formatDate";
import formatNumber from "../../utils/formatNumber";
import loadingButton from "@/utils/loadingButton";
import VueFeather from 'vue-feather';
import StkModal from "@/components/modals/Stk-Modal.vue";
import {decrypt} from "@/utils/crypto";

export default {
  name: "Billing",
  components: {
    VueFeather,
    StkModal
  },

  data(){
    return {
      loading: true,
      billing: JSON.parse(sessionStorage.getItem('billing')) ?? {
        payment: {
          details: {}
        }
      },
      billings: [{
        payment: {
          details: {}
        }
      }],
      subscription: {
        package: {}
      },
    }
  },

  methods: {
    formatNumber,
    formatDate,
    getPackageType(packageName) {
      switch(packageName.toLowerCase()) {
        case 'free':
          return 'secondary';
        case 'basic':
          return 'info';
        case 'standard':
          return 'primary';
        case 'enterprise':
          return 'success';
        default:
          return 'primary';
      }
    },
    printReceipt(e, billing) {
      loadingButton(e)
      sessionStorage.setItem('billing', JSON.stringify(billing))
      let body = document.getElementById('printReceipt').innerHTML;
      let originalContents = document.body.innerHTML;
      document.body.innerHTML = body;
      window.print();
      document.body.innerHTML = originalContents;
      window.location.reload();
      loadingButton(e, false)
      return false;
    },
    openPaymentModal() {
      this.$refs.stkModal.stkModalOpen();
    },
    getSubscription: async function () {
      if (sessionStorage.getItem('paymentSuccess') === 'true' || sessionStorage.getItem('subscription') === null) {
        this.subscription = await subscriptionService.getSubscription()
      } else {
        this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
      }
    },
  },

  created: async function () {
    let billings = await subscriptionService.getSubscriptions()
    this.billings = billings.filter(function(item){ return item['payment'] !== null })
    await this.getSubscription()
    this.loading = false
  },
}
</script>

<style scoped>
/* Icon styles */
.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-circle-sm {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Badge styles */
.badge-lg {
  font-size: 0.85rem;
  padding: 0.35rem 0.65rem;
}

.badge-sm {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  white-space: nowrap;
}

.table td {
  vertical-align: middle;
}

/* Button hover effects */
.btn-success.rounded-pill:hover,
.btn-outline-dark.rounded-pill:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

/* Print styles */
@media screen {
  #printReceipt {
    display: none;
  }
}

@media print {
  .no-print, .no-print * {
    display: none !important;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.9rem;
  }
}
</style>
